/**
 * Price Intelligence Page
 * 
 * Document-grounded price analysis interface.
 * 
 * NON-NEGOTIABLE:
 * - Locked data only (PDF, PO, Xero bills, bank records)
 * - No recommendations
 * - Full source traceability
 * - Category-first intelligence
 */
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { 
  Wallet, PiggyBank, TrendingUp, Receipt, BarChart3, FileText, Link2,
  Search, AlertTriangle, Info, ChevronLeft, Building2, Calendar,
  ArrowUpRight, ArrowDownRight, RefreshCw
} from 'lucide-react';
import axios from 'axios';
import GlassPanel from '../../components/GlassPanel';
import { useAuth } from '../../contexts/AuthContext';
import { AIExplanationBox } from '../../components/AIComponents';
import {
  CategoryCard,
  AllowanceDriftAlert,
  PriceRecordRow,
  SupplierComparisonTable,
  DataSourcesSummary,
  PriceSourceBadge
} from '../../components/PriceIntelligenceComponents';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const financeSubNav = [
  { path: '/finance', label: 'Overview', icon: Wallet },
  { path: '/finance/pots', label: 'Money Pots', icon: PiggyBank },
  { path: '/finance/cashflow', label: 'Cashflow', icon: TrendingUp },
  { path: '/finance/invoices', label: 'Invoices', icon: Receipt },
  { path: '/finance/margin', label: 'Margin', icon: BarChart3 },
  { path: '/finance/documents', label: 'Documents', icon: FileText },
  { path: '/finance/price-intelligence', label: 'Price Intel', icon: Search },
  { path: '/finance/xero', label: 'Xero', icon: Link2 },
];

export function PriceIntelligence() {
  const location = useLocation();
  const navigate = useNavigate();
  const { category: categoryParam } = useParams();
  const { token, orgId } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(categoryParam || null);
  const [categoryDetail, setCategoryDetail] = useState(null);
  const [error, setError] = useState(null);

  // Fetch overview data
  useEffect(() => {
    const fetchOverview = async () => {
      if (!token || !orgId) return;
      
      try {
        setLoading(true);
        const [overviewRes, categoriesRes] = await Promise.all([
          axios.get(`${API_URL}/api/price-intelligence/overview`, {
            params: { org_id: orgId },
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API_URL}/api/price-intelligence/categories`, {
            params: { org_id: orgId },
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        
        setOverview(overviewRes.data);
        setCategories(categoriesRes.data.categories || []);
        setError(null);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load price intelligence');
      } finally {
        setLoading(false);
      }
    };

    fetchOverview();
  }, [token, orgId]);

  // Fetch category detail when selected
  useEffect(() => {
    const fetchCategoryDetail = async () => {
      if (!token || !orgId || !selectedCategory) {
        setCategoryDetail(null);
        return;
      }
      
      try {
        const response = await axios.get(
          `${API_URL}/api/price-intelligence/category/${selectedCategory}`,
          {
            params: { org_id: orgId },
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        setCategoryDetail(response.data);
      } catch (err) {
        console.error('Failed to fetch category detail:', err);
      }
    };

    fetchCategoryDetail();
  }, [token, orgId, selectedCategory]);

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
  };

  const handleBackToOverview = () => {
    setSelectedCategory(null);
    setCategoryDetail(null);
  };

  if (loading) {
    return (
      <div className="page">
        <div style={{ textAlign: 'center', padding: 'var(--space-3xl)' }}>
          <RefreshCw size={24} className="animate-spin" style={{ color: 'var(--accent-primary)' }} />
          <div style={{ marginTop: 'var(--space-md)', color: 'var(--text-muted)' }}>
            Loading price intelligence...
          </div>
        </div>
      </div>
    );
  }

  // Category Detail View
  if (selectedCategory && categoryDetail) {
    return (
      <div className="page">
        {/* Header */}
        <div className="page-header">
          <div className="page-header-content">
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
              <button 
                onClick={handleBackToOverview}
                className="btn btn-secondary"
                style={{ padding: 'var(--space-sm)' }}
              >
                <ChevronLeft size={20} />
              </button>
              <div>
                <h1>{categoryDetail.category_label}</h1>
                <p>{categoryDetail.total_records} price records from verified sources</p>
              </div>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="responsive-grid-3" style={{ gap: 'var(--space-lg)', marginBottom: 'var(--space-xl)' }}>
          <div style={{
            background: 'var(--glass-bg)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-lg)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 'var(--space-xs)' }}>
              Lowest Price
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--status-success)' }}>
              £{(categoryDetail.summary?.lowest_price || 0).toFixed(2)}
            </div>
            {categoryDetail.summary?.lowest_price_source && (
              <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginTop: 'var(--space-xs)' }}>
                {categoryDetail.summary.lowest_price_source.source_supplier}
              </div>
            )}
          </div>

          <div style={{
            background: 'var(--glass-bg)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-lg)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 'var(--space-xs)' }}>
              Highest Price
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--status-error)' }}>
              £{(categoryDetail.summary?.highest_price || 0).toFixed(2)}
            </div>
            {categoryDetail.summary?.highest_price_source && (
              <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginTop: 'var(--space-xs)' }}>
                {categoryDetail.summary.highest_price_source.source_supplier}
              </div>
            )}
          </div>

          <div style={{
            background: 'var(--glass-bg)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-lg)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 'var(--space-xs)' }}>
              Most Recent
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>
              £{(categoryDetail.summary?.most_recent_price || 0).toFixed(2)}
            </div>
            {categoryDetail.summary?.most_recent_source && (
              <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginTop: 'var(--space-xs)' }}>
                {new Date(categoryDetail.summary.most_recent_source.price_date).toLocaleDateString('en-GB')}
              </div>
            )}
          </div>
        </div>

        {/* Supplier Comparison */}
        {categoryDetail.supplier_comparison && (
          <GlassPanel title="Supplier Comparison" titleIcon={Building2} className="mb-xl">
            <SupplierComparisonTable comparison={categoryDetail.supplier_comparison} />
          </GlassPanel>
        )}

        {/* All Records */}
        <GlassPanel title="Price Records" titleIcon={FileText}>
          {categoryDetail.records && categoryDetail.records.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--bg-secondary)' }}>
                    <th style={{ padding: 'var(--space-sm)', textAlign: 'left', fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)' }}>Description</th>
                    <th style={{ padding: 'var(--space-sm)', textAlign: 'right', fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)' }}>Unit Price</th>
                    <th style={{ padding: 'var(--space-sm)', textAlign: 'right', fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)' }}>Total</th>
                    <th style={{ padding: 'var(--space-sm)', textAlign: 'left', fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)' }}>Supplier</th>
                    <th style={{ padding: 'var(--space-sm)', textAlign: 'left', fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)' }}>Date</th>
                    <th style={{ padding: 'var(--space-sm)', textAlign: 'left', fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)' }}>Source</th>
                  </tr>
                </thead>
                <tbody>
                  {categoryDetail.records.map((record) => (
                    <PriceRecordRow key={record.id} record={record} />
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: 'var(--space-xl)', color: 'var(--text-muted)' }}>
              No price records found for this category
            </div>
          )}
        </GlassPanel>

        <AIExplanationBox className="mt-xl">
          All prices shown are from verified source documents (PDF quotes, purchase orders, Xero bills, bank records).
          No prices are estimated, averaged, or inferred. Click any record to trace back to its source.
        </AIExplanationBox>
      </div>
    );
  }

  // Overview View
  return (
    <div className="page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-content">
          <h1>Price Intelligence</h1>
          <p>Document-verified pricing insights</p>
        </div>
      </div>

      {/* Sub Navigation */}
      <div className="flex gap-sm mb-xl" style={{ flexWrap: 'wrap' }}>
        {financeSubNav.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`btn ${location.pathname === item.path ? 'btn-primary' : 'btn-secondary'}`}
            style={{ gap: 'var(--space-sm)' }}
          >
            <item.icon size={16} />
            {item.label}
          </Link>
        ))}
      </div>

      {/* Non-Negotiable Rules */}
      <div 
        data-testid="price-rules-notice"
        style={{
          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--space-md)',
          marginBottom: 'var(--space-lg)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-sm)' }}>
          <Info size={18} style={{ color: 'rgb(59, 130, 246)', flexShrink: 0, marginTop: '2px' }} />
          <div>
            <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '4px' }}>
              How Price Intelligence Works
            </div>
            <ul style={{ 
              margin: 0, 
              paddingLeft: 'var(--space-md)', 
              fontSize: '0.8125rem', 
              color: 'var(--text-secondary)',
              lineHeight: 1.6
            }}>
              <li><strong>Locked data only:</strong> All prices from verified documents (PDF quotes, POs, Xero bills, bank records).</li>
              <li><strong>No recommendations:</strong> We show trends and deltas, never suggest what you should charge.</li>
              <li><strong>Full traceability:</strong> Every price links back to its source document.</li>
            </ul>
          </div>
        </div>
      </div>

      {error && (
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--space-md)',
          marginBottom: 'var(--space-lg)',
          color: 'var(--status-error)'
        }}>
          <AlertTriangle size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
          {error}
        </div>
      )}

      {/* Main Content Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 'var(--space-xl)' }}>
        {/* Left Column */}
        <div>
          {/* Allowance Drifts */}
          {overview?.allowance_drifts && overview.allowance_drifts.length > 0 && (
            <GlassPanel title="Allowance Drift Alerts" titleIcon={AlertTriangle} className="mb-xl">
              <div style={{ marginBottom: 'var(--space-sm)', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                Detected patterns where quote allowances are consistently lower than actual costs.
              </div>
              {overview.allowance_drifts.map((drift) => (
                <AllowanceDriftAlert key={drift.category} drift={drift} />
              ))}
            </GlassPanel>
          )}

          {/* Categories */}
          <GlassPanel title="Material Categories" titleIcon={BarChart3}>
            {categories.length > 0 ? (
              <div className="responsive-grid-2" style={{ gap: 'var(--space-md)' }}>
                {categories.map((category) => (
                  <CategoryCard
                    key={category.category}
                    category={category}
                    onClick={handleCategoryClick}
                  />
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: 'var(--space-xl)', color: 'var(--text-muted)' }}>
                <Search size={48} style={{ marginBottom: 'var(--space-md)', opacity: 0.5 }} />
                <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 'var(--space-xs)' }}>No price data yet</h3>
                <p style={{ fontSize: '0.875rem' }}>
                  Upload supplier quotes or connect Xero to start building price intelligence.
                </p>
                <Link to="/finance/documents" className="btn btn-primary" style={{ marginTop: 'var(--space-md)' }}>
                  <FileText size={16} />
                  Upload Documents
                </Link>
              </div>
            )}
          </GlassPanel>
        </div>

        {/* Right Column */}
        <div>
          {/* Data Sources */}
          <DataSourcesSummary 
            sources={overview?.sources_summary}
            dateRange={{
              earliest: overview?.earliest_record,
              latest: overview?.latest_record
            }}
          />

          {/* Quick Stats */}
          <div style={{
            background: 'var(--glass-bg)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--space-md)',
            marginTop: 'var(--space-lg)'
          }}>
            <h4 style={{ margin: '0 0 var(--space-md)', fontSize: '0.875rem' }}>Summary</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Total Records</span>
                <span style={{ fontWeight: 600 }}>{overview?.total_price_records || 0}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Categories</span>
                <span style={{ fontWeight: 600 }}>{categories.length}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Drift Alerts</span>
                <span style={{ fontWeight: 600, color: overview?.allowance_drifts?.length > 0 ? 'var(--status-warning)' : 'var(--text-primary)' }}>
                  {overview?.allowance_drifts?.length || 0}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AIExplanationBox className="mt-xl">
        Price Intelligence analyzes only document-verified data. AI never suggests prices or makes recommendations.
        All insights can be traced back to their source documents for accountant-grade accuracy.
      </AIExplanationBox>
    </div>
  );
}

export default PriceIntelligence;
